import React from 'react';
import { Checkbox, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import s from './s.module.less';

// 表头逐列筛选（原型 09/09：toolbar 的 search / All Statuses / All Payers 全部移除，
// 每一列自己从表头图标筛自己 —— 两个控件提供同一件事是设计负责人点名的重复）。
//
// antd 的 filterDropdown 本身 portal 到 body，配合自控的 filterDropdownOpen，
// 表格因输入而重渲染时面板不会被关掉。

// ---------- 文本搜索气泡（PATIENT / PROVIDER）----------
export const SearchFilterDropdown = ({
    placeholder,
    value,
    onChange,
}: {
    placeholder: string;
    value: string;
    onChange: (v: string) => void;
}) => (
    <div className={s.dateFilterDropdown}>
        <Input
            autoFocus
            prefix={<SearchOutlined className={s.searchIcon} />}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            allowClear
        />
    </div>
);

export type CheckFilterOption = {
    key: string;
    label: string;
    // STATUS 菜单会带条数，SOURCE / PAYER 不带
    count?: number;
};

// 「什么都没选」与「全部取消」是两回事：前者不筛选（等于全勾），后者要筛出空表。
// 用这个哨兵表示后者，它永远不会是任何一个真实选项的 key。
export const NONE_SELECTED = '__none';

// 当前应该显示为勾选的选项：没选过 = 全勾（与 All chip 一致）；__none = 全不勾
export const checkedKeys = (selected: string[], options: CheckFilterOption[]): string[] => {
    if (selected.includes(NONE_SELECTED)) return [];
    if (!selected.length) return options.map((o) => o.key);
    return selected;
};

// 勾选状态 → 存回 state：全勾归一成 []（不筛选），全不勾存 __none
export const commitChecked = (next: string[], options: CheckFilterOption[]): string[] => {
    if (!next.length) return [NONE_SELECTED];
    if (next.length === options.length) return [];
    return next;
};

// 表头图标旁的数字：只有筛选生效时才显示，标的是「勾了几个」
export const activeCheckedCount = (selected: string[], options: CheckFilterOption[]): number =>
    selected.includes(NONE_SELECTED) ? 0 : checkedKeys(selected, options).length;

// 有没有在筛：没选过 = 没筛
export const isCheckFilterActive = (selected: string[]): boolean => selected.length > 0;

// ---------- 勾选清单（STATUS / PAYER / SOURCE）----------
export const CheckFilterDropdown = ({
    options,
    selected,
    onChange,
    emptyText,
}: {
    options: CheckFilterOption[];
    selected: string[];
    onChange: (next: string[]) => void;
    emptyText: string;
}) => {
    if (!options.length) return <div className={s.filterMenu}>{emptyText}</div>;

    const checked = checkedKeys(selected, options);
    const toggle = (key: string, on: boolean) => {
        const next = on ? [...checked, key] : checked.filter((k) => k !== key);
        onChange(commitChecked(next, options));
    };

    return (
        <div className={s.filterMenu}>
            <div className={s.filterMenuTools}>
                <button
                    type="button"
                    disabled={checked.length === options.length}
                    onClick={() => onChange(commitChecked(options.map((o) => o.key), options))}
                >
                    Select all
                </button>
                <button type="button" disabled={checked.length === 0} onClick={() => onChange([NONE_SELECTED])}>
                    Clear
                </button>
            </div>
            {options.map((o) => (
                <label key={o.key} className={s.filterMenuRow}>
                    <Checkbox checked={checked.includes(o.key)} onChange={(e) => toggle(o.key, e.target.checked)} />
                    <span className={s.filterMenuLabel}>{o.label}</span>
                    {o.count != null && <span className={s.filterMenuCount}>{o.count}</span>}
                </label>
            ))}
        </div>
    );
};
